import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { IpService } from '../ip.service';
import { HeadersService } from '../api/headers/headers.service';
import { EventTypeName, TrackParameters, TrackProperties, TrackUserDetails, UserTrackerBody } from '../../../models/user-tracker';
import { DbImpacter, TrackImpacter } from '../../../models/impacter/impacter';
import { TrackPositions } from '../../../models/impacter/impacter-position';
import { TenderEventType } from '../../directives/behaviourDirective/tracking.directive';
import { UserSessionStore } from '../user-session.store';

@Injectable({
  providedIn: 'root',
})
export class UserTrackerService {
  constructor(
    private http: HttpClient,
    private router: Router,
    private ipService: IpService,
    private sessionStore: UserSessionStore,
    private headersService: HeadersService
  ) {}

  private responseBodyBuildMethodsMap = new Map<string, (eventBody: any) => any>([
    [EventTypeName.IMPACTER_SEARCH, eventBody => this.buildResponseBodyIMPACTER_SEARCH(eventBody)],
    [EventTypeName.IMPACTER_OPENING, eventBody => this.buildResponseBodyIMPACTER_OPENING(eventBody)],
    [EventTypeName.IMPACTER_ARTICLE_SEARCH, eventBody => this.buildResponseBodyIMPACTER_ARTICLE_SEARCH(eventBody)],
  ]);

  private parametersBuildMethodsMap = new Map<string, (params: HttpParams) => any>([
    [EventTypeName.IMPACTER_SEARCH, () => this.buildParametersIMPACTER_SEARCH()],
    [EventTypeName.IMPACTER_OPENING, () => this.buildParametersIMPACTER_OPENING()],
    [EventTypeName.DOCUMENT_OPENING_FROM_ALERT, params => this.buildParametersDOCUMENT_OPENING_FROM_ALERT(params)],
  ]);

  track(body: UserTrackerBody | any) {
    return this.http.post(`${environment.explainApiGatewayUrl}tracking`, body);
  }

  trackEvent(eventType: EventTypeName | TenderEventType, eventName: string, eventLocation = '', eventValue = '', eventDetail = '') {
    const session = this.sessionStore.userSession();
    if (!session) {
      return;
    }
    const body = {
      event_type: eventType,
      event_timestamp: new Date().toISOString(),
      user_id: session.id,
      email: session.email,
      event_location: eventLocation,
      event_name: eventName,
      event_value: eventValue.toString(),
      event_detail: eventDetail.toString(),
    };
    this.track(body).toPromise();
  }

  buildUserTrackerJSON(request: HttpRequest<any>, event: HttpResponse<any>): UserTrackerBody | null {
    const eventTypeName = this.getEventTypeName(request);
    if (eventTypeName) {
      const userTrackerBody = new UserTrackerBody(eventTypeName);

      userTrackerBody.properties = this.buildProperties(request);

      if (this.parametersBuildMethodsMap.has(eventTypeName)) {
        const getParams = this.parametersBuildMethodsMap.get(eventTypeName);
        if (getParams) {
          userTrackerBody.parameters = getParams(request.params);
        }
      } else {
        userTrackerBody.parameters = {};
      }

      userTrackerBody.request_headers = this.buildHeaders(request.headers);
      userTrackerBody.request_body = request.body;
      userTrackerBody.response_headers = this.buildHeaders(event.headers);

      if (this.responseBodyBuildMethodsMap.has(eventTypeName)) {
        const getResponseBody = this.responseBodyBuildMethodsMap.get(eventTypeName);
        if (getResponseBody) {
          userTrackerBody.response_body = getResponseBody(event.body);
        }
      } else {
        userTrackerBody.response_body = event.body;
      }

      return userTrackerBody;
    }
    return null;
  }

  getEventTypeName(request: HttpRequest<any>): string | null {
    return request.headers.get('tracked-method-name');
  }

  buildProperties(request: HttpRequest<any>): TrackProperties {
    const consumedEndpointType = request.method;
    const consumedEndpointUrl = request.urlWithParams;
    const currentFrontendUrl = this.buildCurrentFrontUrl();
    const ip = this.getUserIp();
    const userDetails = new TrackUserDetails(this.getUserExplainToken(), this.getUserEmail());

    return new TrackProperties(consumedEndpointType, consumedEndpointUrl, currentFrontendUrl, ip, userDetails);
  }

  buildBasicUserInformations() {
    const session = this.sessionStore.userSession();
    return {
      user_id: session?.id ?? null,
      group_account_id: session?.groupAccountId ?? null,
      email: session?.email ?? null,
    };
  }

  getTrackUserDetails() {
    return new TrackUserDetails(this.getUserExplainToken(), this.getUserEmail());
  }

  getUserIp(): string {
    return this.ipService.UserIp;
  }
  getUserExplainToken(): string {
    return this.sessionStore.explainToken();
  }
  getUserEmail(): string | undefined {
    return this.sessionStore.userSession()?.email?.trim().toLowerCase();
  }
  getRouterUrl(): string {
    return this.router.url;
  }

  buildParametersIMPACTER_SEARCH(): TrackParameters {
    const trackParams = new TrackParameters();
    trackParams.exemple = 'exemple';

    return trackParams;
  }

  buildParametersIMPACTER_OPENING(): TrackParameters {
    const array = this.getRouterUrl().split('/');
    const trackParams = new TrackParameters();
    trackParams.search_territory_uid = array[array.findIndex(val => val === 'search') + 1];
    trackParams.impacter_id = parseInt(array[array.findIndex(val => val === 'impacter') + 1], 10);

    return trackParams;
  }

  buildParametersDOCUMENT_OPENING_FROM_ALERT(params: HttpParams): TrackParameters {
    const trackParams = new TrackParameters();
    const rank = params.get('rank');
    const articleCount = params.get('article_count');
    const adminDocCount = params.get('admin_doc_count');
    const documentType = params.get('document_type');
    const openSourceId = params.get('open_source_id');
    const collectiveOrderId = params.get('collective_order_id');

    if (rank) {
      trackParams.rank = +rank;
    }
    if (articleCount) {
      trackParams.article_count = parseInt(articleCount, 10);
    }
    if (adminDocCount) {
      trackParams.admin_doc_count = parseInt(adminDocCount, 10);
    }
    if (documentType) {
      trackParams.document_type = documentType;
    }
    if (openSourceId) {
      trackParams.territory_watch_id = parseInt(openSourceId, 10);
    }
    if (collectiveOrderId) {
      trackParams.collective_order_id = parseInt(collectiveOrderId, 10);
    }

    return trackParams;
  }

  buildHeaders(httpHeaders: HttpHeaders) {
    return this.headersService.jsonFromHttpHeaders(httpHeaders);
  }

  buildResponseBodyIMPACTER_SEARCH(eventBody: any) {
    return eventBody;
  }

  buildResponseBodyIMPACTER_OPENING(eventBody: DbImpacter): TrackImpacter {
    const impacter = eventBody;
    // @ts-ignore - tslint throw an error but it's allowed. Booleans are converted to 1 and 0s.
    const orderedPositions = impacter.positions.sort((a, b) => a.is_old - b.is_old);

    return new TrackImpacter(impacter.id, impacter.name, new TrackPositions(orderedPositions));
  }

  buildResponseBodyIMPACTER_ARTICLE_SEARCH(eventBody: any) {
    eventBody['territory_count'] = this.flattenObject(eventBody['territory_count']);
    return eventBody;
  }

  buildCurrentFrontUrl() {
    return `${environment.appBaseUrl}` + this.router.url;
  }

  flattenObject(toFlatten: Object): { keys: Array<any>; values: Array<any> } {
    return { keys: Object.keys(toFlatten), values: Object.values(toFlatten) };
  }
}
